import type { AppointmentRow, SchedulingData } from "../domain/scheduling.js";
import "./scheduling.css";

const root = document.querySelector<HTMLElement>("[data-scheduling]");
if (root) {
  const data = JSON.parse(root.dataset.schedulingData ?? "{}") as SchedulingData;
  const form = root.querySelector<HTMLFormElement>("[data-appointment-form]");
  const dialog = root.querySelector<HTMLDialogElement>("#appointment-modal");
  const time = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
  const minutes = (value: string) => { const [h, m] = value.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
  const field = (name: string) => form?.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
  function error(target: HTMLElement, message: string) { target.textContent = message; target.hidden = false; target.focus(); }
  async function save(url: string, method: string, input?: unknown) {
    const response = await fetch(url, { method, headers: { "Content-Type": "application/json" }, ...(input ? { body: JSON.stringify(input) } : {}) });
    const result = await response.json().catch(() => null) as { message?: string } | null;
    if (!response.ok) throw new Error(result?.message ?? "The change could not be saved. Please try again.");
    window.location.reload();
  }
  function updateInsurance(preselect?: string) {
    if (!form) return;
    const insurance = field("paymentType")?.value === "insurance";
    const fields = form.querySelector<HTMLFieldSetElement>("[data-insurance-fields]")!;
    fields.hidden = !insurance; fields.disabled = !insurance;
    form.querySelector<HTMLElement>("[data-insurance-completion]")!.hidden = !insurance;
    const select = field("authorizationService") as HTMLSelectElement;
    const selected = preselect ?? select.value;
    select.replaceChildren(new Option("Select authorization and service", ""));
    for (const a of data.authorizations.filter(a => a.patientId === field("patientId")?.value && a.treatmentId === field("treatmentId")?.value)) {
      const option = new Option(`${a.serviceCode} · ${a.startDate}–${a.endDate} · ${a.remainingUnits} remaining (${a.plannedUnits} planned)`, `${a.id}:${a.serviceCodeId}`);
      select.add(option);
    }
    select.value = selected;
    const detail = form.querySelector<HTMLElement>("[data-authorization-detail]")!;
    detail.textContent = "Booking checks the authorization dates, assigned therapist, and available units.";
    updateCompletedVisit();
  }
  function updateCompletedVisit(preselect?: string) {
    if (!form) return;
    const enabled = field("paymentType")?.value === "insurance" && field("status")?.value === "completed";
    const select = field("completedVisitId") as HTMLSelectElement;
    const selected = preselect ?? select.value;
    form.querySelector<HTMLElement>("[data-completed-visit]")!.hidden = !enabled;
    select.disabled = !enabled; select.required = enabled;
    select.replaceChildren(new Option("Select completed visit", ""));
    const [authorizationId, serviceCodeId] = (field("authorizationService")?.value ?? "").split(":");
    for (const visit of data.completedVisits.filter(v => v.patientId === field("patientId")?.value && v.therapistUserId === field("therapistUserId")?.value && v.serviceDate === field("appointmentDate")?.value && v.authorizationId === authorizationId && v.serviceCodeId === serviceCodeId)) {
      select.add(new Option(`${visit.serviceDate} · ${visit.units} units · ${visit.id.slice(0, 8)}`, visit.id));
    }
    select.value = selected;
  }
  function open(appointment?: AppointmentRow) {
    if (!form || !dialog) return;
    form.reset();
    const output = form.querySelector<HTMLElement>("[data-form-error]")!; output.hidden = true;
    field("id")!.value = appointment?.id ?? "";
    if (appointment) {
      for (const name of ["patientId", "paymentType", "therapistUserId", "treatmentId", "appointmentDate", "status", "plannedUnits"] as const) {
        const control = field(name); if (control) control.value = String(appointment[name] ?? "");
      }
      field("startTime")!.value = time(appointment.startMinute); field("endTime")!.value = time(appointment.endMinute);
    } else {
      const query = new URLSearchParams(window.location.search);
      if (query.get("patientId")) field("patientId")!.value = query.get("patientId")!;
      if (query.get("therapistId")) field("therapistUserId")!.value = query.get("therapistId")!;
    }
    updateInsurance(appointment?.authorizationId ? `${appointment.authorizationId}:${appointment.serviceCodeId}` : "");
    updateCompletedVisit(appointment?.completedVisitId ?? "");
    dialog.showModal(); field("patientId")?.focus();
  }
  root.querySelector("[data-new-appointment]")?.addEventListener("click", () => open());
  for (const button of root.querySelectorAll<HTMLElement>("[data-appointment-id]")) {
    button.addEventListener("click", () => open(data.appointments.find(a => a.id === button.dataset.appointmentId)));
    button.addEventListener("dragstart", event => { (event as DragEvent).dataTransfer?.setData("text/plain", button.dataset.appointmentId ?? ""); });
  }
  form?.addEventListener("change", event => { if (event.target instanceof HTMLSelectElement && ["patientId", "paymentType", "treatmentId"].includes(event.target.name)) updateInsurance(); else updateCompletedVisit(); });
  form?.addEventListener("submit", async event => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(form));
    const input: Record<string, unknown> = { patientId: values.patientId, therapistUserId: values.therapistUserId, treatmentId: values.treatmentId, appointmentDate: values.appointmentDate, paymentType: values.paymentType, status: values.status, startMinute: minutes(String(values.startTime)), endMinute: minutes(String(values.endTime)) };
    if (values.paymentType === "insurance") { const [authorizationId, serviceCodeId] = String(values.authorizationService).split(":"); Object.assign(input, { authorizationId, serviceCodeId, plannedUnits: Number(values.plannedUnits) }); }
    if (values.completedVisitId) input.completedVisitId = values.completedVisitId;
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!; button.disabled = true;
    try { await save(`/api/scheduling/appointments${values.id ? `/${encodeURIComponent(String(values.id))}` : ""}`, values.id ? "PATCH" : "POST", input); }
    catch (e) { error(form.querySelector<HTMLElement>("[data-form-error]")!, e instanceof Error ? e.message : "Unable to save appointment."); }
    finally { button.disabled = false; }
  });
  let moving = false;
  for (const slot of root.querySelectorAll<HTMLElement>("[data-schedule-slot]")) {
    slot.addEventListener("dragover", event => { event.preventDefault(); slot.classList.add("drag-over"); });
    slot.addEventListener("dragleave", () => slot.classList.remove("drag-over"));
    slot.addEventListener("drop", async event => {
      event.preventDefault(); slot.classList.remove("drag-over"); if (moving) return;
      const id = (event as DragEvent).dataTransfer?.getData("text/plain");
      const a = data.appointments.find(a => a.id === id); if (!a || a.status !== "scheduled") return;
      const startMinute = Number(slot.dataset.minute);
      moving = true;
      const input = { patientId: a.patientId, treatmentId: a.treatmentId, paymentType: a.paymentType, status: a.status, appointmentDate: slot.dataset.date, therapistUserId: slot.dataset.therapist, startMinute, endMinute: startMinute + a.endMinute - a.startMinute, ...(a.paymentType === "insurance" ? { authorizationId: a.authorizationId, serviceCodeId: a.serviceCodeId, plannedUnits: a.plannedUnits } : {}) };
      try { await save(`/api/scheduling/appointments/${encodeURIComponent(a.id)}`, "PATCH", input); }
      catch (e) { error(root!.querySelector<HTMLElement>("[data-schedule-error]")!, e instanceof Error ? e.message : "Unable to move appointment."); }
      finally { moving = false; }
    });
  }
  const availability = root.querySelector<HTMLFormElement>("[data-availability-form]");
  availability?.addEventListener("submit", async event => {
    event.preventDefault(); const values = Object.fromEntries(new FormData(availability));
    const button = availability.querySelector<HTMLButtonElement>('button[type="submit"]')!; button.disabled = true;
    try { await save("/api/scheduling/availability", "POST", { therapistUserId: values.therapistUserId, weekday: Number(values.weekday), startMinute: minutes(String(values.startTime)), endMinute: minutes(String(values.endTime)) }); }
    catch (e) { error(availability.querySelector<HTMLElement>("[data-form-error]")!, e instanceof Error ? e.message : "Unable to save hours."); }
    finally { button.disabled = false; }
  });
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-delete-availability]")) button.addEventListener("click", async () => {
    button.disabled = true;
    try { await save(`/api/scheduling/availability/${encodeURIComponent(button.dataset.deleteAvailability!)}`, "DELETE"); }
    catch (e) { error(root!.querySelector<HTMLElement>("[data-schedule-error]")!, e instanceof Error ? e.message : "Unable to remove hours."); }
    finally { button.disabled = false; }
  });
  if (new URLSearchParams(window.location.search).get("action") === "book") open();
}
